import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState, Pill,
    CopperButton, SlateButton, Field, TextInput, NumberInput, Select, Modal,
} from "@/components/common/UI";
import { toast } from "sonner";
import { fmtINR, fmtPct } from "@/lib/format";
import { ArrowLeft, ArrowRight, Plus, X, Sparkles, Info, AlertTriangle, Send, Check } from "lucide-react";

const TABS = [
    { key: "setup", label: "Setup" },
    { key: "cost", label: "Cost Build-Up" },
    { key: "oh", label: "Overhead & ICC" },
    { key: "profit", label: "Profit" },
    { key: "summary", label: "Price Summary" },
    { key: "approval", label: "Approval" },
];

const emptyForm = {
    customer_id: "",
    enquiry_date: new Date().toISOString().slice(0, 10),
    response_due_date: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
    parts: [],
    bop_items: [],
    manual_items: [],
    fes_supply_type: "ON_COWL",
    notes: "",
};

export default function RFQForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [tab, setTab] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [pricing, setPricing] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [zbcs, setZbcs] = useState([]);
    const [constants, setConstants] = useState(null);
    const [bopCatalogue, setBopCatalogue] = useState([]);
    const [partModal, setPartModal] = useState(false);
    const [partSearch, setPartSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [previewing, setPreviewing] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/customers"), api.get("/zbcs"), api.get("/rfqs/constants"),
            api.get("/bop-price-catalogue").catch(() => ({ data: [] })),
        ]).then(([c, z, k, bc]) => { setCustomers(c.data); setZbcs(z.data); setConstants(k.data); setBopCatalogue(bc.data); });
        if (isEdit) {
            api.get(`/rfqs/${id}`).then((r) => {
                const x = r.data;
                setForm({
                    customer_id: x.customer_id,
                    enquiry_date: x.enquiry_date ? x.enquiry_date.slice(0, 10) : "",
                    response_due_date: x.response_due_date ? x.response_due_date.slice(0, 10) : "",
                    parts: x.parts || [],
                    bop_items: x.bop_items || [],
                    manual_items: x.manual_items || [],
                    fes_supply_type: x.fes_supply_type || "ON_COWL",
                    notes: x.notes || "",
                });
                setPricing(x.pricing);
                setLoading(false);
            }).catch(() => setLoading(false));
        }
    }, [id, isEdit]);

    const customer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
    const norm = customer?.current_norm || {};
    const isVECV = !!(norm.overhead_fixed || norm.profit_fixed);
    const variant = customer?.formula_variant || "STANDARD";

    const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

    const buildPayload = () => ({
        customer_id: form.customer_id,
        enquiry_date: `${form.enquiry_date}T00:00:00Z`,
        response_due_date: `${form.response_due_date}T00:00:00Z`,
        parts: (form.parts || []).map((p) => ({
            zbc_id: p.zbc_id, part_number: p.part_number || "", part_name: p.part_name || "",
            qty_per_assy: parseInt(p.qty_per_assy, 10) || 1,
            annual_volume: parseInt(p.annual_volume, 10) || 0,
        })),
        bop_items: (form.bop_items || []).map((b) => ({
            description: b.description, vendor: b.vendor || "",
            unit_price: parseFloat(b.unit_price) || 0, qty: parseFloat(b.qty) || 1,
        })),
        manual_items: (form.manual_items || []).map((m) => ({
            item: m.item, type: m.type || "OTHER", amount: parseFloat(m.amount) || 0,
            notes: m.notes || null,
            bop_catalogue_id: m.bop_catalogue_id || null,
            unit_rate: m.unit_rate != null ? parseFloat(m.unit_rate) : null,
            qty: m.qty != null ? parseFloat(m.qty) : null,
        })),
        fes_supply_type: form.fes_supply_type,
        notes: form.notes || null,
    });

    // Live price preview: debounced, non-persisting server call. Fires whenever
    // parts/BOP/manual items/customer change, so the price panel updates as the
    // user edits without requiring a Save Draft round-trip.
    useEffect(() => {
        if (!form.customer_id || (form.parts || []).length === 0) return;
        const handle = setTimeout(() => {
            setPreviewing(true);
            api.post("/rfqs/preview", buildPayload())
                .then((r) => setPricing(r.data.pricing))
                .catch(() => {})
                .finally(() => setPreviewing(false));
        }, 400);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.customer_id, form.parts, form.bop_items, form.manual_items, form.fes_supply_type]);

    const save = async () => {
        if (!form.customer_id) { toast.error("Pick a customer first"); setTab(0); return; }
        if ((form.parts || []).length === 0) { toast.error("Add at least one part"); setTab(0); return; }
        setSubmitting(true);
        try {
            const payload = buildPayload();
            const { data } = isEdit ? await api.put(`/rfqs/${id}`, payload) : await api.post("/rfqs", payload);
            setPricing(data.pricing);
            toast.success(isEdit ? "RFQ updated" : `Created ${data.reference}`);
            if (!isEdit) navigate(`/rfq/${data.id}/edit`);
            return data;
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    const submitForApproval = async () => {
        const saved = await save();
        if (!saved) return;
        try {
            const { data } = await api.post(`/rfqs/${saved.id}/submit`);
            toast.success(`Submitted, routed to ${data.new_status === "PENDING_DH" ? "Department Head" : "Director"} (margin ${data.margin_pct}%)`);
            navigate(`/rfq/${saved.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading) return <LoadingState />;

    // Available ZBCs filtered to current customer
    const availableZbcs = zbcs.filter((z) =>
        z.customer_id === form.customer_id &&
        z.status === "VERIFIED" &&
        !form.parts.some((p) => p.zbc_id === z.id) &&
        (!partSearch || z.part_number.toLowerCase().includes(partSearch.toLowerCase()) || z.part_name.toLowerCase().includes(partSearch.toLowerCase()))
    );

    const addPart = (z) => {
        setF({ parts: [...form.parts, { zbc_id: z.id, part_number: z.part_number, part_name: z.part_name, should_cost: z.should_cost, qty_per_assy: 1, annual_volume: 5000 }] });
        setPartModal(false); setPartSearch("");
    };
    const addAllParts = () => {
        const additions = availableZbcs.map((z) => ({
            zbc_id: z.id, part_number: z.part_number, part_name: z.part_name,
            should_cost: z.should_cost, qty_per_assy: 1, annual_volume: 5000,
        }));
        if (additions.length === 0) return;
        setF({ parts: [...form.parts, ...additions] });
        toast.success(`Added ${additions.length} part${additions.length === 1 ? "" : "s"}`);
        setPartModal(false); setPartSearch("");
    };
    const removePart = (idx) => setF({ parts: form.parts.filter((_, i) => i !== idx) });
    const updatePart = (idx, patch) => {
        const a = [...form.parts]; a[idx] = { ...a[idx], ...patch }; setF({ parts: a });
    };

    const addBop = () => setF({ bop_items: [...form.bop_items, { description: "", vendor: "", unit_price: 0, qty: 1 }] });
    const updateBop = (i, p) => { const a = [...form.bop_items]; a[i] = { ...a[i], ...p }; setF({ bop_items: a }); };
    const removeBop = (i) => setF({ bop_items: form.bop_items.filter((_, x) => x !== i) });

    const addManual = (type = "OTHER", item = "") => setF({ manual_items: [...form.manual_items, { item, type, amount: 0, notes: "" }] });
    const updateManual = (i, p) => { const a = [...form.manual_items]; a[i] = { ...a[i], ...p }; setF({ manual_items: a }); };
    const removeManual = (i) => setF({ manual_items: form.manual_items.filter((_, x) => x !== i) });

    return (
        <div data-testid="rfq-form-page">
            <PageHeader
                eyebrow={isEdit ? "EDIT RFQ" : "NEW RFQ"}
                title={isEdit ? "Edit Commercial Offer" : "New Commercial Offer"}
                subtitle="Pick ZBC parts, add BOP and manual items, review commercials. Customer norms auto-apply."
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton variant="outline" onClick={() => navigate("/rfq")}>Cancel</SlateButton>
                        <SlateButton onClick={save} disabled={submitting} variant="outline">Save Draft</SlateButton>
                        <CopperButton onClick={submitForApproval} disabled={submitting}>
                            <Send className="h-4 w-4" /> Submit for Approval
                        </CopperButton>
                    </div>
                }
            />

            {/* Tab bar */}
            <div className="border-b border-slate-200 mb-5 flex gap-0 overflow-x-auto">
                {TABS.map((t, i) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(i)}
                        data-testid={`rfq-tab-${t.key}`}
                        className={[
                            "relative -mb-px px-4 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors",
                            tab === i ? "text-[hsl(var(--copper))] border-b-2 border-[hsl(var(--copper))]" : "text-slate-500 hover:text-slate-700",
                        ].join(" ")}
                    >
                        <span className="font-mono text-[10px] text-slate-400 mr-1.5">{i + 1}</span>{t.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-12 gap-5">
                <section className="col-span-12 lg:col-span-8">
                    {tab === 0 && (
                        <Card className="p-6 space-y-5">
                            <h2 className="font-display text-xl font-semibold">Setup</h2>
                            <Field label="Customer" required>
                                <Select value={form.customer_id} onChange={(e) => setF({ customer_id: e.target.value })}>
                                    <option value="">Select customer…</option>
                                    {customers.filter((c) => c.is_active).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                                </Select>
                            </Field>

                            {customer && (
                                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-[12px] text-blue-900">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Sparkles className="h-3.5 w-3.5" />
                                        <span className="font-semibold">Customer commercials auto-loaded</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono tabular-nums">
                                        <div><div className="text-[10px] uppercase tracking-wider text-blue-700">Variant</div><div className="font-semibold">{variant}</div></div>
                                        <div><div className="text-[10px] uppercase tracking-wider text-blue-700">ICC %</div><div>{fmtPct(norm.icc_pct || 0)}</div></div>
                                        <div><div className="text-[10px] uppercase tracking-wider text-blue-700">Overhead</div><div>{isVECV ? fmtINR(norm.overhead_fixed || 0, 0) : fmtPct(norm.overhead_pct || 0)}</div></div>
                                        <div><div className="text-[10px] uppercase tracking-wider text-blue-700">Profit</div><div>{isVECV ? fmtINR(norm.profit_fixed || 0, 0) : fmtPct(norm.profit_pct || 0)}</div></div>
                                        <div><div className="text-[10px] uppercase tracking-wider text-blue-700">Discount %</div><div>{fmtPct(norm.discount_pct || 0)}</div></div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Enquiry Date" required><TextInput type="date" value={form.enquiry_date} onChange={(e) => setF({ enquiry_date: e.target.value })} /></Field>
                                <Field label="Response Due Date" required><TextInput type="date" value={form.response_due_date} onChange={(e) => setF({ response_due_date: e.target.value })} /></Field>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Parts in this RFQ</span>
                                    <SlateButton variant="outline" onClick={() => setPartModal(true)} disabled={!form.customer_id} className="text-xs">
                                        <Plus className="h-3.5 w-3.5" /> Add Part from ZBC
                                    </SlateButton>
                                </div>
                                {form.parts.length === 0 ? (
                                    <p className="text-[12px] text-slate-500 italic">No parts yet. {!form.customer_id ? "Select a customer first." : "Add ZBC parts to build the RFQ."}</p>
                                ) : (
                                    <div className="rounded-md border border-slate-200 overflow-hidden">
                                        <table className="w-full text-[12px]">
                                            <thead className="bg-slate-50">
                                                <tr><th className="text-left py-2 px-3 eyebrow">Part Number</th><th className="text-left py-2 px-3 eyebrow">Name</th><th className="text-right py-2 px-3 eyebrow">Should-Cost</th><th className="text-right py-2 px-3 eyebrow">Qty/Assy</th><th className="text-right py-2 px-3 eyebrow">Annual Vol</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {form.parts.map((p, i) => (
                                                    <tr key={i} className="border-t border-slate-100">
                                                        <td className="py-1.5 px-3 font-mono font-semibold">{p.part_number}</td>
                                                        <td className="py-1.5 px-3">{p.part_name}</td>
                                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(p.should_cost || 0)}</td>
                                                        <td className="py-1.5 px-3"><NumberInput value={p.qty_per_assy} onChange={(e) => updatePart(i, { qty_per_assy: e.target.value })} className="h-7 text-[12px]" min="1" /></td>
                                                        <td className="py-1.5 px-3"><NumberInput value={p.annual_volume} onChange={(e) => updatePart(i, { annual_volume: e.target.value })} className="h-7 text-[12px]" min="0" /></td>
                                                        <td className="py-1.5 px-2"><button onClick={() => removePart(i)} className="h-6 w-6 text-red-500 hover:bg-red-50 rounded-md flex items-center justify-center"><X className="h-3.5 w-3.5" /></button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    {tab === 1 && (
                        <Card className="p-6 space-y-5">
                            <h2 className="font-display text-xl font-semibold">Cost Build-Up</h2>
                            <InfoBox>ZBC costs auto-sum here. Add bought-out parts and manual items (paint, packing, hardware) that aren&apos;t captured in the per-piece ZBC.</InfoBox>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Bought-Out Parts (BOP)</span>
                                    <SlateButton variant="outline" onClick={addBop} className="text-xs"><Plus className="h-3.5 w-3.5" /> Add BOP</SlateButton>
                                </div>
                                {form.bop_items.map((b, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2 p-2 rounded-md border border-slate-200">
                                        <div className="col-span-4"><Field label="Description"><TextInput value={b.description} onChange={(e) => updateBop(i, { description: e.target.value })} className="h-8 text-[12px]" /></Field></div>
                                        <div className="col-span-3"><Field label="Vendor"><TextInput value={b.vendor} onChange={(e) => updateBop(i, { vendor: e.target.value })} className="h-8 text-[12px]" /></Field></div>
                                        <div className="col-span-2"><Field label="Unit ₹"><NumberInput value={b.unit_price} onChange={(e) => updateBop(i, { unit_price: e.target.value })} className="h-8 text-[12px]" step="0.01" /></Field></div>
                                        <div className="col-span-1"><Field label="Qty"><NumberInput value={b.qty} onChange={(e) => updateBop(i, { qty: e.target.value })} className="h-8 text-[12px]" step="1" /></Field></div>
                                        <div className="col-span-1 font-mono text-[12px] tabular-nums font-semibold text-[hsl(var(--copper))]">{fmtINR((parseFloat(b.unit_price)||0) * (parseFloat(b.qty)||0))}</div>
                                        <div className="col-span-1 flex justify-end"><button onClick={() => removeBop(i)} className="h-8 w-8 text-red-500 hover:bg-red-50 rounded-md flex items-center justify-center"><X className="h-4 w-4" /></button></div>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Manual Cost Items</span>
                                    <div className="flex gap-1">
                                        {["PAINT","HYDRAULIC","HARDWARE","PACKAGING","SURFACE_TREATMENT"].map((t) => (
                                            <button key={t} onClick={() => addManual(t, t.replace("_"," "))} className="text-[10px] px-2 py-1 rounded-md border border-slate-200 hover:bg-[hsl(var(--copper-soft))] hover:border-[hsl(var(--copper))]/30">+ {t.replace("_"," ")}</button>
                                        ))}
                                    </div>
                                </div>
                                {form.manual_items.map((m, i) => {
                                    const catalogued = m.type === "PAINT" || m.type === "HYDRAULIC";
                                    const catalogueOptions = bopCatalogue.filter((c) => c.category === m.type && c.is_active);
                                    const pickCatalogueItem = (catId) => {
                                        const c = bopCatalogue.find((x) => x.id === catId);
                                        if (!c) { updateManual(i, { bop_catalogue_id: "" }); return; }
                                        const qty = parseFloat(m.qty) || 1;
                                        updateManual(i, {
                                            bop_catalogue_id: c.id, item: c.name, unit_rate: c.rate,
                                            qty, amount: Math.round(c.rate * qty * 100) / 100,
                                        });
                                    };
                                    const setQty = (q) => {
                                        const qty = parseFloat(q) || 0;
                                        const rate = parseFloat(m.unit_rate) || 0;
                                        updateManual(i, { qty, amount: m.bop_catalogue_id ? Math.round(rate * qty * 100) / 100 : m.amount });
                                    };
                                    return (
                                        <div key={i} className="mb-2 p-2 rounded-md border border-slate-200">
                                            <div className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-3"><Field label="Item"><TextInput value={m.item} onChange={(e) => updateManual(i, { item: e.target.value })} className="h-8 text-[12px]" /></Field></div>
                                                <div className="col-span-2"><Field label="Type"><Select value={m.type} onChange={(e) => updateManual(i, { type: e.target.value, bop_catalogue_id: "" })} className="h-8 text-[12px]"><option>PAINT</option><option>HYDRAULIC</option><option>HARDWARE</option><option>PACKAGING</option><option>SURFACE_TREATMENT</option><option>OTHER</option></Select></Field></div>
                                                <div className="col-span-2"><Field label="Amount ₹"><NumberInput value={m.amount} onChange={(e) => updateManual(i, { amount: e.target.value })} className="h-8 text-[12px]" step="0.01" /></Field></div>
                                                <div className="col-span-4"><Field label="Notes"><TextInput value={m.notes || ""} onChange={(e) => updateManual(i, { notes: e.target.value })} className="h-8 text-[12px]" /></Field></div>
                                                <div className="col-span-1 flex justify-end"><button onClick={() => removeManual(i)} className="h-8 w-8 text-red-500 hover:bg-red-50 rounded-md flex items-center justify-center"><X className="h-4 w-4" /></button></div>
                                            </div>
                                            {catalogued && (
                                                <div className="grid grid-cols-12 gap-2 items-end mt-2">
                                                    <div className="col-span-6">
                                                        <Field label={`${m.type === "PAINT" ? "Paint" : "Hydraulic"} Catalogue Item`} helper="Auto-fills Amount from the BOP Catalogue, editable after">
                                                            <Select value={m.bop_catalogue_id || ""} onChange={(e) => pickCatalogueItem(e.target.value)} className="h-8 text-[12px]" data-testid={`rfq-manual-catalogue-${i}`}>
                                                                <option value="">Type manually…</option>
                                                                {catalogueOptions.map((c) => <option key={c.id} value={c.id}>{c.code} · ₹{c.rate}/{c.uom?.replace("PER_", "").toLowerCase()}</option>)}
                                                            </Select>
                                                        </Field>
                                                    </div>
                                                    <div className="col-span-2"><Field label="Qty"><NumberInput value={m.qty ?? ""} onChange={(e) => setQty(e.target.value)} className="h-8 text-[12px]" step="1" /></Field></div>
                                                    <div className="col-span-4 text-[11px] text-slate-500">
                                                        {m.bop_catalogue_id && <>₹{m.unit_rate} × {m.qty || 0} = <span className="font-mono font-semibold text-[hsl(var(--copper))]">{fmtINR(m.amount || 0)}</span></>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    )}

                    {tab === 2 && pricing && <CommercialsView pricing={pricing} norm={norm} isVECV={isVECV} />}
                    {tab === 3 && pricing && <ProfitView pricing={pricing} variant={variant} fesSupplyType={form.fes_supply_type} onFesChange={(v) => setF({ fes_supply_type: v })} constants={constants} />}
                    {tab === 4 && pricing && <PriceSummary pricing={pricing} />}
                    {tab === 5 && (
                        <Card className="p-6">
                            <h2 className="font-display text-xl font-semibold mb-1">Approval</h2>
                            <p className="text-[12px] text-slate-500 mb-3">Save the RFQ first, then click <strong>Submit for Approval</strong> in the page header. Routing is automatic based on margin.</p>
                            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <div>RFQ must be saved as draft before submission. Margin {">="} {constants?.dh_threshold}% routes to Dept Head; below routes to Director.</div>
                            </div>
                        </Card>
                    )}
                    {(tab >= 2 && tab <= 4) && !pricing && (
                        <Card className="p-6"><p className="text-sm text-slate-500">Pick a customer and add at least one part in Setup to see the commercial breakdown.</p></Card>
                    )}

                    {/* Tab nav */}
                    <div className="flex items-center justify-between mt-5">
                        <SlateButton variant="outline" onClick={() => setTab(Math.max(0, tab - 1))} disabled={tab === 0}><ArrowLeft className="h-4 w-4" /> Back</SlateButton>
                        <span className="text-[12px] text-slate-500">Tab {tab + 1} of {TABS.length}</span>
                        <CopperButton onClick={() => setTab(Math.min(TABS.length - 1, tab + 1))} disabled={tab === TABS.length - 1}>Next <ArrowRight className="h-4 w-4" /></CopperButton>
                    </div>
                </section>

                {/* Right live price */}
                <aside className="col-span-12 lg:col-span-4">
                    <Card className="p-4 sticky top-20">
                        <div className="eyebrow text-slate-500 mb-3 flex items-center gap-1.5">
                            LIVE PRICE SUMMARY
                            {previewing && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--copper))] animate-pulse" title="Updating…" />}
                        </div>
                        {pricing ? (
                            <>
                                <PriceRow label="RM + BOP" value={pricing.rm_bop_total} />
                                <PriceRow label="Process (Conv+Cut+Weld)" value={pricing.process_total} />
                                <PriceRow label="Overhead" value={pricing.overhead_amount} />
                                <PriceRow label="ICC" value={pricing.icc_amount} />
                                <PriceRow label="Rejection" value={pricing.rejection_amount} />
                                {pricing.tool_amount > 0 && <PriceRow label="Tool Maint" value={pricing.tool_amount} />}
                                <PriceRow label="Profit" value={pricing.profit_amount} />
                                {pricing.discount_amount > 0 && <PriceRow label="Discount" value={-pricing.discount_amount} muted />}
                                {pricing.volume_rebate > 0 && <PriceRow label="Volume Rebate" value={-pricing.volume_rebate} muted />}
                                <div className="border-t border-slate-300 mt-2 pt-3 bg-[hsl(var(--copper-soft))] -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
                                    <div className="eyebrow text-slate-600">FINAL PRICE EX-WORKS</div>
                                    <div className="font-display text-2xl font-semibold text-[hsl(var(--copper))] font-mono tabular-nums animate-calc-pulse" key={pricing.final_price}>
                                        {fmtINR(pricing.final_price)}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Pill tone={pricing.margin_pct >= 15 ? "emerald" : pricing.margin_pct >= 8 ? "amber" : "red"}>
                                            Margin {fmtPct(pricing.margin_pct)}
                                        </Pill>
                                        {pricing.below_dh_threshold && <Pill tone="red">Director approval needed</Pill>}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="text-[12px] text-slate-500">Pick a customer and add at least one part to see live pricing.</p>
                        )}
                    </Card>
                </aside>
            </div>

            <Modal open={partModal} onClose={() => setPartModal(false)} title="Add Part from ZBC" size="xl">
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <TextInput value={partSearch} onChange={(e) => setPartSearch(e.target.value)} placeholder="Search verified ZBCs for this customer…" autoFocus className="flex-1" />
                        <SlateButton
                            variant="outline"
                            onClick={addAllParts}
                            disabled={availableZbcs.length === 0}
                            className="text-xs whitespace-nowrap"
                            data-testid="rfq-add-all-parts"
                        >
                            Add All ({availableZbcs.length})
                        </SlateButton>
                    </div>
                    {availableZbcs.length === 0 ? (
                        <p className="text-[12px] text-slate-500 italic p-4 text-center">No verified ZBCs available for this customer.</p>
                    ) : (
                        <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
                            <table className="w-full text-[12px]">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr><th className="text-left py-2 px-3 eyebrow">Part #</th><th className="text-left py-2 px-3 eyebrow">Name</th><th className="text-right py-2 px-3 eyebrow">Should-Cost</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {availableZbcs.map((z) => (
                                        <tr key={z.id} className="border-t border-slate-100 hover:bg-[hsl(var(--copper-soft))]">
                                            <td className="py-1.5 px-3 font-mono font-semibold">{z.part_number}</td>
                                            <td className="py-1.5 px-3">{z.part_name}</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(z.should_cost || 0)}</td>
                                            <td className="py-1.5 px-2"><CopperButton onClick={() => addPart(z)} className="h-7 px-2.5 text-xs">Add</CopperButton></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function PriceRow({ label, value, muted }) {
    return (
        <div className="flex items-baseline justify-between py-1">
            <span className={`text-[12px] ${muted ? "text-slate-400" : "text-slate-600"}`}>{label}</span>
            <span className={`font-mono tabular-nums text-[13px] ${muted ? "text-slate-400" : "text-slate-700"}`}>{fmtINR(value || 0)}</span>
        </div>
    );
}

function InfoBox({ children }) {
    return <div className="rounded-md border border-blue-200 bg-blue-50 p-3 flex items-start gap-2"><Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" /><div className="text-[12px] text-blue-900 leading-snug">{children}</div></div>;
}

function CommercialsView({ pricing, norm, isVECV }) {
    return (
        <Card className="p-6 space-y-4">
            <h2 className="font-display text-xl font-semibold">Overhead & ICC</h2>
            <InfoBox>These are auto-loaded from the customer&apos;s commercial norm. To change them, update the norm in Foundation → Customers.</InfoBox>
            <table className="w-full text-[13px]">
                <tbody>
                    <tr><td className="py-2 text-slate-600">Overhead Basis</td><td className="text-right font-mono">{pricing.overhead_basis_label}</td></tr>
                    <tr><td className="py-2 text-slate-600">Overhead {isVECV ? "(VECV fixed ₹)" : `(${pricing.overhead_pct}%)`} applied on</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.overhead_basis_amount)}</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-2 font-semibold">Overhead Amount</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(pricing.overhead_amount)}</td></tr>
                    <tr><td className="py-2 text-slate-600">Rejection ({pricing.rejection_pct}%)</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.rejection_amount)}</td></tr>
                    <tr><td className="py-2 text-slate-600">ICC ({pricing.icc_pct}% on RM)</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.icc_amount)}</td></tr>
                    {pricing.tool_amount > 0 && <tr><td className="py-2 text-slate-600">Tool Maintenance ({pricing.tool_pct}%)</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.tool_amount)}</td></tr>}
                    <tr className="border-t border-slate-300"><td className="py-2 font-semibold">Total Additions</td><td className="text-right font-mono tabular-nums font-bold text-[hsl(var(--copper))]">{fmtINR(pricing.additions_total)}</td></tr>
                </tbody>
            </table>
        </Card>
    );
}

function ProfitView({ pricing, variant, fesSupplyType, onFesChange, constants }) {
    return (
        <Card className="p-6 space-y-4">
            <h2 className="font-display text-xl font-semibold">Profit</h2>
            <InfoBox>Profit follows the customer&apos;s formula variant. {variant === "FES_LYNX" && "Pick the FES supply type below: it determines the profit %."}</InfoBox>

            {variant === "FES_LYNX" && constants && (
                <Field label="FES Supply Type" required>
                    <Select value={fesSupplyType} onChange={(e) => onFesChange(e.target.value)}>
                        {Object.entries(constants.fes_supply_types).map(([k, v]) => <option key={k} value={k}>{v.label} ({v.profit_pct}%)</option>)}
                    </Select>
                </Field>
            )}

            <table className="w-full text-[13px]">
                <tbody>
                    <tr><td className="py-2 text-slate-600">Variant</td><td className="text-right font-mono">{variant}</td></tr>
                    <tr><td className="py-2 text-slate-600">Profit method</td><td className="text-right font-mono text-[11px]">{pricing.profit_label}</td></tr>
                    {pricing.profit_rm_amount > 0 && <tr><td className="py-2 text-slate-600">Profit on RM+BOP</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.profit_rm_amount)}</td></tr>}
                    {pricing.profit_process_amount > 0 && <tr><td className="py-2 text-slate-600">Profit on Process</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.profit_process_amount)}</td></tr>}
                    <tr className="border-t border-slate-300"><td className="py-2 font-semibold">Total Profit</td><td className="text-right font-mono tabular-nums font-bold text-[hsl(var(--copper))]">{fmtINR(pricing.profit_amount)}</td></tr>
                    {pricing.discount_amount > 0 && <tr><td className="py-2 text-slate-600">Discount ({variant === "VOLVO_EXPORT" ? "Volvo 4%" : "Customer"})</td><td className="text-right font-mono tabular-nums">−{fmtINR(pricing.discount_amount)}</td></tr>}
                    {pricing.volume_rebate > 0 && <tr><td className="py-2 text-slate-600">Volume Rebate (Volvo)</td><td className="text-right font-mono tabular-nums">−{fmtINR(pricing.volume_rebate)}</td></tr>}
                    {variant === "VOLVO_EXPORT" && <tr><td colSpan={2} className="text-[11px] text-slate-500 pt-2 italic">Freight is Volvo&apos;s scope, not included in this price.</td></tr>}
                </tbody>
            </table>
        </Card>
    );
}

function PriceSummary({ pricing }) {
    const a = pricing.agg;
    const grand = pricing.grand_total || 1;
    const pct = (v) => `${((v / grand) * 100).toFixed(1)}%`;
    return (
        <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Complete Price Build-Up</h2>
            <table className="w-full text-[13px]">
                <thead><tr className="border-b border-slate-300 text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">Cost Element</th><th className="text-right py-2">Amount (₹)</th><th className="text-right py-2">% of GT</th></tr></thead>
                <tbody>
                    <tr><td className="py-1.5">A · RM Steel</td><td className="text-right font-mono tabular-nums">{fmtINR(a.rm_steel)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.rm_steel)}</td></tr>
                    <tr><td className="py-1.5">B · RM Tube</td><td className="text-right font-mono tabular-nums">{fmtINR(a.rm_tube)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.rm_tube)}</td></tr>
                    <tr><td className="py-1.5">C · BOP</td><td className="text-right font-mono tabular-nums">{fmtINR(a.bop_total)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.bop_total)}</td></tr>
                    <tr><td className="py-1.5">D · Paint / Surface Treatment</td><td className="text-right font-mono tabular-nums">{fmtINR(a.paint)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.paint)}</td></tr>
                    <tr><td className="py-1.5">E · Hardware / Other</td><td className="text-right font-mono tabular-nums">{fmtINR(a.hardware_other)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.hardware_other)}</td></tr>
                    <tr className="border-t border-slate-200"><td className="py-1.5 font-semibold">Direct Material</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(pricing.rm_bop_total)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.rm_bop_total)}</td></tr>
                    <tr><td className="py-1.5">F · Conversion</td><td className="text-right font-mono tabular-nums">{fmtINR(a.conversion)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.conversion)}</td></tr>
                    <tr><td className="py-1.5">G · Cutting</td><td className="text-right font-mono tabular-nums">{fmtINR(a.cutting)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.cutting)}</td></tr>
                    <tr><td className="py-1.5">H · Welding</td><td className="text-right font-mono tabular-nums">{fmtINR(a.welding)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(a.welding)}</td></tr>
                    <tr className="border-t border-slate-200"><td className="py-1.5 font-semibold">Total Cost of Material &amp; Labour</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(pricing.cost_total)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.cost_total)}</td></tr>
                    <tr><td className="py-1.5">I · Overhead</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.overhead_amount)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.overhead_amount)}</td></tr>
                    <tr><td className="py-1.5">J · Rejection</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.rejection_amount)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.rejection_amount)}</td></tr>
                    <tr><td className="py-1.5">K · ICC</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.icc_amount)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.icc_amount)}</td></tr>
                    {pricing.tool_amount > 0 && <tr><td className="py-1.5">L · Tool Maintenance</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.tool_amount)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.tool_amount)}</td></tr>}
                    <tr><td className="py-1.5">M · Profit</td><td className="text-right font-mono tabular-nums">{fmtINR(pricing.profit_amount)}</td><td className="text-right font-mono text-[11px] text-slate-500">{pct(pricing.profit_amount)}</td></tr>
                    <tr className="border-t-2 border-slate-300"><td className="py-2 font-semibold">Grand Total</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(pricing.grand_total)}</td><td className="text-right text-[11px] text-slate-500">100%</td></tr>
                    {pricing.discount_amount > 0 && <tr><td className="py-1.5 text-slate-600">N · Discount</td><td className="text-right font-mono tabular-nums">({fmtINR(pricing.discount_amount)})</td><td></td></tr>}
                    {pricing.volume_rebate > 0 && <tr><td className="py-1.5 text-slate-600">O · Volume Rebate</td><td className="text-right font-mono tabular-nums">({fmtINR(pricing.volume_rebate)})</td><td></td></tr>}
                    <tr className="border-t-2 border-slate-900 bg-[hsl(var(--copper-soft))]"><td className="py-3 font-display font-semibold text-base">FINAL PRICE EX-WORKS ABC COMPANY</td><td className="text-right font-mono tabular-nums font-bold text-2xl text-[hsl(var(--copper))]">{fmtINR(pricing.final_price)}</td><td></td></tr>
                </tbody>
            </table>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-md border border-slate-200 p-3"><div className="eyebrow text-slate-500">DM %</div><div className="font-mono tabular-nums font-semibold mt-1">{pct(pricing.rm_bop_total)}</div></div>
                <div className="rounded-md border border-slate-200 p-3"><div className="eyebrow text-slate-500">VALUE ADDITION</div><div className="font-mono tabular-nums font-semibold mt-1">{fmtINR(pricing.value_addition)}</div></div>
                <div className="rounded-md border border-slate-200 p-3"><div className="eyebrow text-slate-500">PROFIT</div><div className="font-mono tabular-nums font-semibold mt-1">{fmtINR(pricing.profit_amount)}</div></div>
                <div className="rounded-md border border-slate-200 p-3 bg-[hsl(var(--copper-soft))]"><div className="eyebrow text-slate-600">MARGIN</div><div className="font-mono tabular-nums font-semibold text-[hsl(var(--copper))] mt-1">{fmtPct(pricing.margin_pct)}</div></div>
            </div>
        </Card>
    );
}
